import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Legend, AreaChart, Area
} from "recharts";
import {
    FaServer, FaExclamationTriangle, FaCheckCircle, FaUsers,
    FaBolt, FaSync, FaBell, FaShieldAlt, FaChartLine,
    FaCircle, FaClock, FaTachometerAlt, FaEye, FaCog,
    FaWifi, FaTimesCircle
} from "react-icons/fa";
import { monitoringApi } from "../../api/monitoring.api";
import AlertsPanel from "../../components/AlertsPanel/AlertsPanel";
import AlertRulesModal from "../../components/AlertRulesModal/AlertRulesModal";
import ServiceHealthCard from "../../components/ServiceHealthCard/ServiceHealthCard";
import ErrorLogsPanel from "../../components/ErrorLogsPanel/ErrorLogsPanel";
import DashboardLayout from "../../../../layouts/DashboardLayout";
import "./monitoring.css";

const REFRESH_INTERVAL_MS = 30000; // 30 segundos

function Monitoring() {
    const [overview, setOverview] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [byModule, setByModule] = useState([]);
    const [topUsers, setTopUsers] = useState([]);
    const [activeAlerts, setActiveAlerts] = useState([]);
    const [alertEvents, setAlertEvents] = useState({ content: [], totalElements: 0 });
    const [loading, setLoading] = useState(true);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [sseConnected, setSseConnected] = useState(false);
    const [showAlertsPanel, setShowAlertsPanel] = useState(false);
    const [showRulesModal, setShowRulesModal] = useState(false);
    const [timelineHours, setTimelineHours] = useState(24);
    const sseRef = useRef(null);

    const fetchAll = useCallback(async () => {
        try {
            const [ov, tl, mod, users, alerts, events] = await Promise.allSettled([
                monitoringApi.getOverview(),
                monitoringApi.getTimeline(timelineHours),
                monitoringApi.getByModule(),
                monitoringApi.getTopUsers(10),
                monitoringApi.getActiveAlerts(),
                monitoringApi.getAlertEvents({ page: 0, size: 5, status: 'ACTIVE' }),
            ]);

            if (ov.status === 'fulfilled') setOverview(ov.value);
            if (tl.status === 'fulfilled') setTimeline(tl.value);
            if (mod.status === 'fulfilled') setByModule(mod.value);
            if (users.status === 'fulfilled') setTopUsers(users.value);
            if (alerts.status === 'fulfilled') setActiveAlerts(alerts.value);
            if (events.status === 'fulfilled') setAlertEvents(events.value);

            setLastRefresh(new Date());
        } catch (e) {
            console.error("Error fetching monitoring data:", e);
        } finally {
            setLoading(false);
        }
    }, [timelineHours]);

    // Initial load and polling.
    useEffect(() => {
        fetchAll();
        const interval = setInterval(fetchAll, REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [fetchAll]);

    // SSE connection.
    useEffect(() => {
        const es = monitoringApi.createSseConnection(
            (eventName, data) => {
                if (eventName === 'heartbeat') setSseConnected(true);
                if (eventName === 'metrics') setOverview(prev => ({ ...prev, ...data }));
            },
            () => setSseConnected(false)
        );
        sseRef.current = es;
        setSseConnected(true);

        return () => {
            if (sseRef.current) sseRef.current.close();
        };
    }, []);

    const handleRefresh = () => {
        setLoading(true);
        fetchAll();
    };

    // UI helpers.
    const getSystemStatusColor = (status) => {
        switch (status) {
            case 'HEALTHY': return '#10b981';
            case 'DEGRADED': return '#f59e0b';
            case 'CRITICAL': return '#ef4444';
            default: return '#64748b';
        }
    };

    const getSystemStatusLabel = (status) => {
        switch (status) {
            case 'HEALTHY': return 'Sistema Saludable';
            case 'DEGRADED': return 'Sistema Degradado';
            case 'CRITICAL': return 'Estado Crítico';
            default: return 'Calculando...';
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'CRITICAL': return '#ef4444';
            case 'WARNING': return '#f59e0b';
            default: return '#3b82f6';
        }
    };

    // RENDER.
    if (loading && !overview) {
        return (
            <DashboardLayout>
                <div className="page-container">
                    <div className="noc-loading">
                        <div className="noc-loading-spinner" />
                        <p>Cargando consola NOC y métricas de observabilidad...</p>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="page-container">
                <div className="noc-wrapper">
                    <div className="page-header-row flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="page-title-wrapper">
                            <p className="page-subtitle">Centro de Operaciones de Red y Observabilidad Integral en Tiempo Real.</p>
                        </div>

                        <div className="noc-header-right">
                            <div className={`noc-sse-badge ${sseConnected ? 'connected' : 'disconnected'}`}>
                                <FaWifi />
                                <span>{sseConnected ? 'En vivo' : 'Reconectando...'}</span>
                            </div>

                            {overview?.systemStatus && (
                                <div
                                    className="noc-system-status"
                                    style={{ borderColor: getSystemStatusColor(overview.systemStatus) }}
                                >
                                    <FaCircle style={{ color: getSystemStatusColor(overview.systemStatus), fontSize: '8px' }} />
                                    <span style={{ color: getSystemStatusColor(overview.systemStatus) }}>
                                        {getSystemStatusLabel(overview.systemStatus)}
                                    </span>
                                </div>
                            )}

                            {lastRefresh && (
                                <span className="noc-last-refresh">
                                    <FaClock /> {lastRefresh.toLocaleTimeString()}
                                </span>
                            )}

                            <button className="noc-btn-secondary" onClick={() => setShowRulesModal(true)}>
                                <FaCog /> Reglas
                            </button>

                            <button
                                className="noc-btn-alert"
                                onClick={() => setShowAlertsPanel(!showAlertsPanel)}
                                title="Ver Alertas Activas"
                            >
                                <FaBell />
                                {activeAlerts.length > 0 && (
                                    <span className="noc-alert-badge">{activeAlerts.length}</span>
                                )}
                            </button>

                            <button className="noc-btn-primary" onClick={handleRefresh} disabled={loading}>
                                <FaSync className={loading ? 'spinning' : ''} /> Actualizar
                            </button>
                        </div>
                    </div>

                    {/* KPI CARDS */}
                    <div className="noc-kpi-grid">
                        <KpiCard
                            icon={<FaChartLine />}
                            label="Eventos 24h"
                            value={overview?.totalEvents24h ?? '—'}
                            color="#1F2B68"
                            sub={`${overview?.successEvents24h ?? 0} exitosos`}
                        />
                        <KpiCard
                            icon={<FaExclamationTriangle />}
                            label="Tasa de Error"
                            value={`${overview?.errorRatePercent ?? 0}%`}
                            color={overview?.errorRatePercent > 20 ? '#EF4444' : overview?.errorRatePercent > 10 ? '#D97706' : '#10B981'}
                            sub={`${overview?.errorEvents24h ?? 0} errores hoy`}
                        />
                        <KpiCard
                            icon={<FaUsers />}
                            label="Usuarios Activos"
                            value={overview?.activeUsers24h ?? '—'}
                            color="#0284C7"
                            sub="en las últimas 24h"
                        />
                        <KpiCard
                            icon={<FaServer />}
                            label="Servicios Activos"
                            value={overview?.activeServices ?? '—'}
                            color="#10B981"
                            sub="con actividad reciente"
                        />
                        <KpiCard
                            icon={<FaBolt />}
                            label="Eventos / 5 min"
                            value={overview?.eventsLast5min ?? '—'}
                            color="#7C3AED"
                            sub="últimos 5 minutos"
                        />
                        <KpiCard
                            icon={<FaExclamationTriangle />}
                            label="Alertas Activas"
                            value={overview?.activeAlerts ?? activeAlerts.length}
                            color={overview?.criticalAlerts > 0 ? '#EF4444' : overview?.warningAlerts > 0 ? '#D97706' : '#10B981'}
                            sub={`${overview?.criticalAlerts ?? 0} críticas · ${overview?.warningAlerts ?? 0} advertencias`}
                        />
                    </div>

                    {/* TIMELINE CHART */}
                    <div className="noc-section">
                        <div className="noc-section-header">
                            <h2><FaChartLine /> Eventos por hora</h2>
                            <div className="noc-timeline-controls">
                                {[6, 12, 24, 48, 168].map(h => (
                                    <button
                                        key={h}
                                        className={`noc-time-btn ${timelineHours === h ? 'active' : ''}`}
                                        onClick={() => setTimelineHours(h)}
                                    >
                                        {h < 24 ? `${h}h` : h === 24 ? '1d' : h === 48 ? '2d' : '7d'}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="noc-chart-container">
                            <ResponsiveContainer width="100%" height={220}>
                                <AreaChart data={timeline} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                                    <defs>
                                        <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#1F2B68" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#1F2B68" stopOpacity={0.0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                                    <XAxis
                                        dataKey="hour"
                                        tick={{ fill: '#64748B', fontSize: 11 }}
                                        tickFormatter={v => v ? v.substring(11, 16) : ''}
                                    />
                                    <YAxis tick={{ fill: '#64748B', fontSize: 11 }} />
                                    <Tooltip
                                        contentStyle={{ background: '#FFFFFF', border: '1px solid #CBD5E1', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                                        labelStyle={{ color: '#0F172A', fontWeight: 600 }}
                                        itemStyle={{ color: '#1F2B68' }}
                                    />
                                    <Area type="monotone" dataKey="total" stroke="#1F2B68" fill="url(#totalGrad)"
                                        strokeWidth={2} name="Total eventos" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* MODULE BREAKDOWN + TOP USERS */}
                    <div className="noc-two-col">
                        <div className="noc-section">
                            <div className="noc-section-header">
                                <h2><FaServer /> Actividad por módulo</h2>
                            </div>
                            <div className="noc-chart-container">
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart data={byModule} layout="vertical"
                                        margin={{ top: 5, right: 20, bottom: 5, left: 80 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                                        <XAxis type="number" tick={{ fill: '#64748B', fontSize: 11 }} />
                                        <YAxis type="category" dataKey="module"
                                            tick={{ fill: '#64748B', fontSize: 11 }} width={75} />
                                        <Tooltip
                                            contentStyle={{ background: '#FFFFFF', border: '1px solid #CBD5E1', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                                            labelStyle={{ color: '#0F172A', fontWeight: 600 }}
                                        />
                                        <Legend wrapperStyle={{ color: '#64748B', fontSize: 12 }} />
                                        <Bar dataKey="successEvents" name="Exitosos" fill="#10B981" radius={[0, 4, 4, 0]} />
                                        <Bar dataKey="errorEvents" name="Errores" fill="#EF4444" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="noc-section">
                            <div className="noc-section-header">
                                <h2><FaUsers /> Top usuarios activos</h2>
                            </div>
                            <div className="noc-users-list">
                                {topUsers.length === 0 ? (
                                    <p className="noc-empty">Sin datos disponibles</p>
                                ) : (
                                    topUsers.map((u, i) => (
                                        <div key={u.username} className="noc-user-row">
                                            <span className="noc-user-rank">#{i + 1}</span>
                                            <span className="noc-user-name">{u.username}</span>
                                            <div className="noc-user-bar-wrap">
                                                <div
                                                    className="noc-user-bar"
                                                    style={{
                                                        width: `${Math.min(100, (u.eventCount / (topUsers[0]?.eventCount || 1)) * 100)}%`
                                                    }}
                                                />
                                            </div>
                                            <span className="noc-user-count">{u.eventCount}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* SERVICE HEALTH */}
                    <div className="noc-section">
                        <div className="noc-section-header">
                            <h2><FaEye /> Estado de servicios</h2>
                        </div>
                        <div className="noc-service-grid">
                            {(overview?.serviceHealth || []).map(s => (
                                <ServiceHealthCard key={s.serviceName} service={s} />
                            ))}
                            {(!overview?.serviceHealth || overview.serviceHealth.length === 0) && (
                                <p className="noc-empty">Sin datos de servicios aún. Los servicios aparecerán cuando registren actividad.</p>
                            )}
                        </div>
                    </div>

                    {/* RECENT ALERTS */}
                    {activeAlerts.length > 0 && (
                        <div className="noc-section">
                            <div className="noc-section-header">
                                <h2><FaBell /> Alertas activas</h2>
                                <button className="noc-btn-link" onClick={() => setShowAlertsPanel(true)}>
                                    Ver todas
                                </button>
                            </div>
                            <div className="noc-recent-alerts">
                                {activeAlerts.slice(0, 5).map(alert => (
                                    <div
                                        key={alert.id}
                                        className="noc-alert-row"
                                        style={{ borderLeftColor: getSeverityColor(alert.severity) }}
                                    >
                                        <FaExclamationTriangle style={{ color: getSeverityColor(alert.severity) }} />
                                        <div className="noc-alert-info">
                                            <span className="noc-alert-name">{alert.ruleName}</span>
                                            <span className="noc-alert-msg">{alert.message}</span>
                                        </div>
                                        <span
                                            className="noc-severity-badge"
                                            style={{ background: getSeverityColor(alert.severity) + '18', color: getSeverityColor(alert.severity) }}
                                        >
                                            {alert.severity}
                                        </span>
                                        <span className="noc-alert-time">
                                            {alert.triggeredAt ? new Date(alert.triggeredAt).toLocaleTimeString() : ''}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* LOG HISTORY */}
                    <div className="noc-section">
                        <div className="noc-section-header">
                            <h2><FaExclamationTriangle /> Historial de Logs y Diagnóstico de Errores</h2>
                            <span className="noc-section-badge">Tiempo real</span>
                        </div>
                        <p className="noc-section-desc">
                            Registro detallado de eventos del sistema. Haz clic en cualquier fila para ver el
                            diagnóstico completo, mensaje interpretable y sugerencias de corrección.
                        </p>
                        <ErrorLogsPanel />
                    </div>

                </div>
            </div>

            {/* PANELS / MODALS */}
            {showAlertsPanel && (
                <AlertsPanel
                    activeAlerts={activeAlerts}
                    onClose={() => setShowAlertsPanel(false)}
                    onRefresh={fetchAll}
                />
            )}

            {showRulesModal && (
                <AlertRulesModal
                    onClose={() => setShowRulesModal(false)}
                    onSave={fetchAll}
                />
            )}
        </DashboardLayout>
    );
}

// === KPI Card sub-component ===

function KpiCard({ icon, label, value, color, sub }) {
    return (
        <div className="noc-kpi-card">
            <div className="noc-kpi-icon" style={{ color: color || '#1F2B68', backgroundColor: (color || '#1F2B68') + '15' }}>
                {icon}
            </div>
            <div className="noc-kpi-body">
                <span className="noc-kpi-value">{value}</span>
                <span className="noc-kpi-label">{label}</span>
                {sub && <span className="noc-kpi-sub">{sub}</span>}
            </div>
        </div>
    );
}

export default Monitoring;
